import React, { useCallback, useMemo } from 'react';

import { Box, Checkbox, CircularProgress, FormControlLabel, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import ResponsiveImage from '~/components/Common/Elements/ResponsiveImage';

import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import { HotelOffer } from '../../hooks/useHotelOffersByPlaceIds';

interface Props {
  offers: Array<HotelOffer>;
  selectedOfferIds: Array<string>;
  onSelectionChange: (selectedIds: Array<string>) => void;
  isLoading?: boolean;
  savedOfferIds?: Array<string>;
}

type OfferRow = HotelOffer & {
  isSaved?: boolean;
};

export default function TagAssignmentsHotelList({
  offers,
  selectedOfferIds,
  onSelectionChange,
  isLoading = false,
  savedOfferIds = [],
}: Props) {
  const { paginationModel, setPaginationModel } = useDataGridPagination({
    pageSize: 25,
    page: 0,
  });

  const selectedOfferIdsSet = useMemo(() => new Set(selectedOfferIds), [selectedOfferIds]);
  const savedOfferIdsSet = useMemo(() => new Set(savedOfferIds), [savedOfferIds]);

  const allSelected = useMemo(() => {
    if (offers.length === 0) {
      return false;
    }
    const selectableOffers = offers.filter((offer) => !savedOfferIdsSet.has(offer.id));
    if (selectableOffers.length === 0) {
      return false;
    }
    return selectableOffers.every((offer) => selectedOfferIdsSet.has(offer.id));
  }, [offers, selectedOfferIdsSet, savedOfferIdsSet]);

  const someSelected = useMemo(() => {
    if (offers.length === 0) {
      return false;
    }
    const selectableOffers = offers.filter((offer) => !savedOfferIdsSet.has(offer.id));
    return selectableOffers.some((offer) => selectedOfferIdsSet.has(offer.id)) && !allSelected;
  }, [offers, selectedOfferIdsSet, savedOfferIdsSet, allSelected]);

  const handleToggleSelectAll = useCallback(() => {
    const currentOfferIdsSet = new Set(offers.map((offer) => offer.id));

    if (allSelected) {
      const remainingSelected = selectedOfferIds.filter((id) => {
        if (savedOfferIdsSet.has(id)) {
          return true;
        }
        if (!currentOfferIdsSet.has(id)) {
          return true;
        }
        return false;
      });
      onSelectionChange(remainingSelected);
    } else {
      const selectableIds = offers.filter((offer) => !savedOfferIdsSet.has(offer.id)).map((offer) => offer.id);
      const newSelected = [...new Set([...selectedOfferIds, ...selectableIds])];
      onSelectionChange(newSelected);
    }
  }, [allSelected, offers, selectedOfferIds, savedOfferIdsSet, onSelectionChange]);

  const handleToggleOffer = useCallback(
    (offerId: string) => {
      if (savedOfferIdsSet.has(offerId)) {
        return;
      }
      const newSelected = selectedOfferIdsSet.has(offerId)
        ? selectedOfferIds.filter((id) => id !== offerId)
        : [...selectedOfferIds, offerId];
      onSelectionChange(newSelected);
    },
    [selectedOfferIds, selectedOfferIdsSet, savedOfferIdsSet, onSelectionChange],
  );

  const rows: Array<OfferRow> = useMemo(() => {
    return offers.map((offer) => ({
      ...offer,
      isSaved: savedOfferIdsSet.has(offer.id),
    }));
  }, [offers, savedOfferIdsSet]);

  const columns: Array<GridColDef<OfferRow>> = useMemo(
    () => [
      {
        field: 'checkbox',
        headerName: '',
        width: 60,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const isSelected = selectedOfferIdsSet.has(params.row.id);
          const isSaved = params.row.isSaved;
          return (
            <Checkbox
              checked={isSelected}
              onChange={() => handleToggleOffer(params.row.id)}
              onClick={(e) => e.stopPropagation()}
              disabled={isSaved}
            />
          );
        },
      },
      {
        field: 'id',
        headerName: 'ID',
        width: 200,
        flex: 1,
      },
      {
        field: 'image',
        headerName: 'Image',
        width: 150,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          if (params.row.imageId) {
            return <ResponsiveImage imageId={params.row.imageId} />;
          }
          return (
            <Box display="flex" alignItems="center" justifyContent="center" width={100} height={75} bgcolor="grey.200">
              <Typography variant="caption" color="text.secondary">
                No Image
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'title',
        headerName: 'Title',
        flex: 2,
        renderCell: (params) => {
          return (
            <Box>
              <Typography variant="body2">{params.value}</Typography>
              {params.row.isSaved && (
                <Typography variant="caption" color="text.secondary">
                  Already assigned
                </Typography>
              )}
            </Box>
          );
        },
      },
    ],
    [selectedOfferIdsSet, handleToggleOffer],
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (offers.length === 0) {
    return (
      <Box p={3}>
        <Typography variant="body2" color="text.secondary">
          No hotel offers found matching your search criteria.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={2}>
        <FormControlLabel
          control={<Checkbox checked={allSelected} indeterminate={someSelected} onChange={handleToggleSelectAll} />}
          label={
            <Typography variant="subtitle1" fontWeight="medium">
              Select All Hotels ({offers.length} offers)
            </Typography>
          }
        />
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        disableRowSelectionOnClick
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        autoHeight
        disableColumnMenu
        getRowClassName={(params) => (params.row.isSaved ? 'MuiDataGrid-row--disabled' : '')}
        sx={{
          '& .MuiDataGrid-row--disabled': {
            opacity: 0.5,
            pointerEvents: 'none',
          },
        }}
      />
    </Box>
  );
}
